/**
 * Where the player's credentials live in the browser.
 *
 * Two modes, matching the server's AUTH_MODE:
 *
 *   bearer — the real one. A launch token from the iframe URL is exchanged
 *            for an access token (short-lived, sent on every request) and a
 *            refresh token (renews the access token).
 *   dev    — local development against AUTH_MODE=dev-header: a player id in
 *            a header, no verification at all.
 *
 * Storage: the access token stays in memory only. The refresh token goes to
 * sessionStorage, so that reloading the page — or the operator resizing the
 * iframe in a way that remounts it — does not end the session; the launch
 * token is single-use and cannot be replayed to get a new one.
 *
 * Not localStorage: sessionStorage dies with the tab, which is the right
 * lifetime for a game session and keeps nothing behind on a shared machine.
 * And nothing is put in a cookie — browsers block third-party cookies in an
 * iframe, so a cookie-based session works locally and dies at the operator.
 */

const REFRESH_STORAGE_KEY = 'slot.refreshToken'

/**
 * How the session started, kept next to the refresh token.
 *
 * A reload resumes the session from the refresh token, and that token
 * looks identical whether it came from a launch or from our own login
 * form. Without this the sign-out control — which only makes sense for the
 * second — would vanish on the first F5.
 */
const AUTH_METHOD_KEY = 'slot.authMethod'

export interface TokenPair {
  accessToken: string
  tokenType: 'Bearer'
  expiresIn: number
  refreshToken: string
  playerId: string
  gameId: string | null
}

interface BearerSession {
  mode: 'bearer'
  accessToken: string
  playerId: string
  /** Epoch ms. Used to refresh proactively rather than after a 401. */
  expiresAt: number
}

interface DevSession {
  mode: 'dev'
  playerId: string
}

type SessionState = BearerSession | DevSession | { mode: 'none' }

let state: SessionState = { mode: 'none' }

/**
 * sessionStorage can throw outright — Safari in "block all cookies" mode,
 * a sandboxed iframe without allow-same-origin. Losing persistence there is
 * acceptable; crashing the game on load is not.
 */
function readStoredRefreshToken(): string | null {
  try {
    return window.sessionStorage.getItem(REFRESH_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredRefreshToken(token: string | null): void {
  safeSetItem(REFRESH_STORAGE_KEY, token)
}

/** sessionStorage can throw outright — private mode, a sandboxed iframe. */
function safeSetItem(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    // Memory-only session. It survives until the page is reloaded.
  }
}

/** In-memory copy: the source of truth when storage is unavailable. */
let refreshToken: string | null = null

export const session = {
  /** Takes what an exchange or a refresh returned. */
  adopt(pair: TokenPair): void {
    state = {
      mode: 'bearer',
      accessToken: pair.accessToken,
      playerId: pair.playerId,
      expiresAt: Date.now() + pair.expiresIn * 1000,
    }
    refreshToken = pair.refreshToken
    writeStoredRefreshToken(pair.refreshToken)
  },

  /** AUTH_MODE=dev-header: the id from POST /players/dev-session. */
  adoptDev(playerId: string): void {
    state = { mode: 'dev', playerId }
  },

  /** Records that this session began with a login and a password. */
  rememberPasswordLogin(): void {
    safeSetItem(AUTH_METHOD_KEY, 'password')
  },

  /** Whether this session — possibly resumed after a reload — began with a form. */
  wasPasswordLogin(): boolean {
    try {
      return window.sessionStorage.getItem(AUTH_METHOD_KEY) === 'password'
    } catch {
      return false
    }
  },

  /** A refresh token from a previous page load, if one survived. */
  storedRefreshToken(): string | null {
    return refreshToken ?? readStoredRefreshToken()
  },

  refreshTokenValue(): string | null {
    return refreshToken ?? readStoredRefreshToken()
  },

  playerId(): string | null {
    return state.mode === 'none' ? null : state.playerId
  },

  isBearer(): boolean {
    return state.mode === 'bearer'
  },

  /**
   * Whether the access token is close enough to expiry to renew it now.
   *
   * The margin exists because a request sent one second before expiry can
   * easily arrive after it — and a round that dies mid-flight is a round
   * the player has to wonder about.
   */
  needsRefresh(marginMs = 30_000): boolean {
    return state.mode === 'bearer' && state.expiresAt - marginMs <= Date.now()
  },

  /** The authentication header for the current mode. */
  headers(): Record<string, string> {
    if (state.mode === 'bearer') return { Authorization: `Bearer ${state.accessToken}` }
    if (state.mode === 'dev') return { 'X-Player-Id': state.playerId }
    return {}
  },

  clear(): void {
    state = { mode: 'none' }
    refreshToken = null
    writeStoredRefreshToken(null)
    safeSetItem(AUTH_METHOD_KEY, null)
  },
}

/**
 * The one-time launch token out of the iframe URL.
 *
 * It is removed from the address bar right away: a URL leaks into history,
 * Referer headers and screenshots, and there is no reason for a spent
 * credential to sit there. The query string is left otherwise intact, so
 * ?game= keeps working.
 */
export function takeLaunchTokenFromUrl(): string | null {
  const url = new URL(window.location.href)
  const token = url.searchParams.get('token')
  if (!token) return null

  url.searchParams.delete('token')
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
  return token
}
